import { Component, OnInit, inject } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { UserAuthenticationService } from "@app/authentication/shared/user-authentication.service";
import { StandingDataType } from "@enums/StandingDataType";
import { TabMenuItem } from "@models/generic/TabModels/TabMenuItem";
import { TabModel } from "@models/generic/TabModels/TabModel";
import { ToastrService } from "ngx-toastr";
import { Observable, defer, firstValueFrom, map } from "rxjs";
import { Language } from "./shared/language.enum";
import { UserCurrentInfoModel } from "./shared/user-current-info-model";
import { UserService } from "./shared/user.service";
import { injectTranslation, provideTranslationScope, TranslatePipe } from "@app/shared/i18n/translation-scope";
import { scope } from "./i18n/scope";
import { CerrixLoaderComponent } from "@app/shared/components/cerrix-loader/cerrix-loader.component";
import { MenuSystemDirective } from "@app/shared/directives/menu-system.directive";
import { CerrixSelectComponent } from "@app/shared/cerrix-select/cerrix-select.component";
import { UserAuditComponent } from "@app/user/user-maintenance/user-detail/user-audit/user-audit.component";
import { UserNotificationSettingsComponent } from "@app/user/shared/notification-settings/user-notification-settings.component";
import { RoleAssignComponent } from "@app/user/user-maintenance/role-assign/role-assign.component";
import { ApiKeyOverviewComponent } from "@app/user/shared/api-key/components/api-key-overview/api-key-overview.component";
import { StandingDataGenericComponent } from "@app/standingdata/generic/standingdata-generic.component";

@Component({
    selector: "user-profile",
    templateUrl: "./user-profile.component.html",
    styleUrls: ["./user-profile.component.scss"],
    viewProviders: [provideTranslationScope(scope)],
    imports: [
        FormsModule,
        CerrixLoaderComponent,
        MenuSystemDirective,
        CerrixSelectComponent,
        UserAuditComponent,
        UserNotificationSettingsComponent,
        RoleAssignComponent,
        ApiKeyOverviewComponent,
        StandingDataGenericComponent,
        TranslatePipe,
    ],
})
export class UserProfileComponent implements OnInit {
    private _ds = inject(UserService);
    private _authDS = inject(UserAuthenticationService);
    private _toastr = inject(ToastrService);
    private _translation = injectTranslation(scope);

    tab: TabModel;
    user: UserCurrentInfoModel;
    passwordChangeModel = {
        oldPassword: "",
        newPassword: "",
        newPasswordConfirm: "",
    };

    ipConfigType;

    // Waits for the scope to load before emitting, so cerrix-select (which snapshots a
    // plain array once) never captures labels from before translations are ready.
    //
    // "Use tenant default" is keyed as 0, not null: ng-select's writeValue treats a null
    // bindValue as "not defined" and refuses to select it, leaving the field blank even
    // though the option exists. The Language enum has no 0 member, so 0 is a safe sentinel.
    // The template converts user.language (null when unset) to/from this sentinel.
    languageOptions$: Observable<{ ID: Language | 0; Name: string }[]> = defer(() =>
        this._translation.ready()
    ).pipe(
        map(() => [
            { ID: 0, Name: this._translation.t("languageOptionUseTenantDefault") },
            { ID: Language.English, Name: this._translation.t("languageOptionEnglish") },
            { ID: Language.Dutch, Name: this._translation.t("languageOptionDutch") },
            { ID: Language.Norwegian, Name: this._translation.t("languageOptionNorwegian") },
        ])
    );

    ngOnInit(): void {
        this._ds.getUserInfo().subscribe((x) => {
            this.user = x;

            if (this.user.manageIPAllowed) {
                this.ipConfigType = StandingDataType.IpWhitelistingPersonal;
            }

            this.tab.showLoader = false;
        });
    }

    // The dropdown shows user.language (null when unset) as the 0 sentinel; convert it back
    // here so the saved value and the resolveEffectiveLanguage fallback stay null-based.
    get languageSelectValue(): Language | 0 {
        return this.user.language ?? 0;
    }

    async languageChanged(newValue: Language | 0, languageUpdatedMessage: string) {
        const language = newValue || null;

        if (this.user.language === language) {
            return;
        }

        await firstValueFrom(this._ds.setLanguage(language));

        this.user.language = language;
        this._toastr.success(languageUpdatedMessage);

        // Per ADR 010: a language change re-bootstraps the whole app, never a live re-render.
        this.reloadPage();
    }

    // Extracted so specs can spy on it: window.location.reload is not spyable in place in
    // every test environment (its property is non-configurable).
    protected reloadPage(): void {
        window.location.reload();
    }

    logout() {
        this._authDS.logout().subscribe((x) => {
            window.location.reload();
        });
    }

    changePassword(passwordUpdatedMessage: string, successTitle: string, updateFailedTitle: string) {
        this._ds.changePassword(this.passwordChangeModel).subscribe((x) => {
            if (!x) {
                this.passwordChangeModel.oldPassword = "";
                this.passwordChangeModel.newPassword = "";
                this.passwordChangeModel.newPasswordConfirm = "";
                this._toastr.success(passwordUpdatedMessage, successTitle);
            } else {
                this._toastr.error(x, updateFailedTitle);
            }
        });
    }

    handleMenuItemClick(menuItem: TabMenuItem) {
        if (!menuItem) {
            return;
        }

        switch (menuItem.menuItemId) {
            case "roles":
                window.dispatchEvent(new Event("resize"));
                break;
        }
    }
}
